import { type Job, TraitementLong } from '@mairistem/composants';
import { getErrorMessage } from '@mairistem/finances-utils';
import { App, Feature, Menu } from '@mairistem/module';
import { request } from '@mairistem/tools';
import * as Sentry from '@sentry/react';
import * as React from 'react';
import { toast } from 'react-toastify';
import ModalAdmin from './components/Admin/ModalAdmin';
import Context from './components/Context';
import IxChangeContext from './components/Context/IxChangeContext';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import Login from './components/Login/Login';
import PolitiqueConfidentialite from './components/PolitiqueConfidentialite/PolitiqueConfidentialite';
import {
	CODE_ACCES_SESSION,
	CODE_CLIENT_SESSION,
	CODE_POSTAL_SESSION,
	COLLECTIVITE_SESSION,
	POLITIQUE_CONFIDENTIALITE_SESSION,
	ROLE_SESSION,
	TOKEN_SESSION,
} from './constants/Login';
import PolitiqueConfidentialiteEnum from './enums/PolitiqueConfidentialiteEnum';
import type { Role } from './interfaces/auth';
import {
	getStructureJobWithBearer,
	getUtilisateurs,
} from './services/utilisateurs';
import parseStructureJob, {
	type ParsedStructureData,
} from './utils/parseStructureJob';

const errorBoundaryFallback = ({
	error,
	resetError,
}: {
	error: unknown;
	resetError: () => void;
}) => (
	<ErrorFallback
		error={error instanceof Error ? error : undefined}
		resetError={resetError}
	/>
);

const clearAllSession = () => {
	sessionStorage.removeItem(COLLECTIVITE_SESSION);
	sessionStorage.removeItem(TOKEN_SESSION);
	sessionStorage.removeItem(CODE_CLIENT_SESSION);
	sessionStorage.removeItem(CODE_POSTAL_SESSION);
	sessionStorage.removeItem(CODE_ACCES_SESSION);
	sessionStorage.removeItem(ROLE_SESSION);
};

const AppIxchange = () => {
	const [token, setToken] = React.useState(
		sessionStorage.getItem(TOKEN_SESSION),
	);
	const [isSessionAuth, setIsSessionAuth] = React.useState(
		Boolean(
			sessionStorage.getItem(CODE_CLIENT_SESSION) &&
				sessionStorage.getItem(CODE_POSTAL_SESSION) &&
				sessionStorage.getItem(CODE_ACCES_SESSION),
		),
	);
	const [politiqueConfidentialite, setPolitiqueConfidentialite] =
		React.useState(localStorage.getItem(POLITIQUE_CONFIDENTIALITE_SESSION));
	const [role, setRole] = React.useState<Role>(
		(sessionStorage.getItem(ROLE_SESSION) as Role) || 'user',
	);
	const [structureData, setStructureData] =
		React.useState<ParsedStructureData | null>(null);
	const [job, setJob] = React.useState<Job | null>(null);
	const [isChangingClient, setIsChangingClient] = React.useState(false);

	const handleResetAuth = () => {
		clearAllSession();
		setToken(null);
		setIsSessionAuth(false);
		setRole('user');
		setStructureData(null);
		setJob(null);
	};

	// Synchroniser role vers sessionStorage
	React.useEffect(() => {
		if (role !== 'user') {
			sessionStorage.setItem(ROLE_SESSION, role);
		} else {
			sessionStorage.removeItem(ROLE_SESSION);
		}
	}, [role]);

	// Au refresh : re-lancer structureJob pour avoir la data fraîche
	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de handleResetAuth
	React.useEffect(() => {
		if (structureData || (!token && !isSessionAuth)) return;

		const fetchStructure = async () => {
			try {
				const codeClient = sessionStorage.getItem(CODE_CLIENT_SESSION);

				if (token && codeClient) {
					const structureJob = await getStructureJobWithBearer(
						token,
						codeClient,
					);
					setJob(structureJob);
				} else if (isSessionAuth) {
					const structureJob = await getUtilisateurs(
						sessionStorage.getItem(CODE_CLIENT_SESSION) ?? '',
						sessionStorage.getItem(CODE_POSTAL_SESSION) ?? '',
						sessionStorage.getItem(CODE_ACCES_SESSION) ?? '',
					);
					setJob(structureJob);
				} else if (token && !codeClient) {
					// Admin refresh avant sélection du codeClient → reset pour re-afficher Login + ModalAdmin
					handleResetAuth();
				}
			} catch (error) {
				toast.error(
					getErrorMessage(error, 'Erreur lors du chargement des données'),
				);
				handleResetAuth();
			}
		};

		fetchStructure();
	}, []);

	const handleLogout = () => {
		handleResetAuth();
		toast.success('Vous avez été déconnecté avec succès');
	};

	const handleChangeClient = () => {
		setIsChangingClient(true);
	};

	const handleChangeClientValidate = async (codeClient: string) => {
		setIsChangingClient(false);

		const previousCodeClient = sessionStorage.getItem(CODE_CLIENT_SESSION);
		sessionStorage.setItem(CODE_CLIENT_SESSION, codeClient);

		try {
			const effectiveToken = token || sessionStorage.getItem(TOKEN_SESSION);
			if (!effectiveToken) return;
			const structureJob = await getStructureJobWithBearer(
				effectiveToken,
				codeClient,
			);
			setStructureData(null);
			setJob(structureJob);
		} catch (error) {
			// Restaurer l'ancien code client pour ne pas laisser l'utilisateur bloqué
			if (previousCodeClient) {
				sessionStorage.setItem(CODE_CLIENT_SESSION, previousCodeClient);
			}
			toast.error(
				getErrorMessage(error, 'Erreur lors du changement de code client'),
			);
		}
	};

	const handleCancelChangeClient = () => {
		setIsChangingClient(false);
	};

	const handleRefreshJobFinish = (finishedJob: Job) => {
		setJob(null);

		if (finishedJob.status === 1) {
			try {
				const parsedData = parseStructureJob(finishedJob.resultat);
				setStructureData(parsedData);
			} catch (error) {
				toast.error(
					getErrorMessage(error, 'Erreur lors du traitement des données'),
				);
				handleResetAuth();
			}
		} else {
			toast.error(finishedJob.resultat || 'Erreur lors du chargement');
			handleResetAuth();
		}
	};

	const handleRefreshJobError = () => {
		toast.error('Erreur lors du chargement des données');
		handleResetAuth();
	};

	const handleAuth = (
		newToken: string,
		data: ParsedStructureData,
		refuseToBeAdmin?: boolean,
	) => {
		setToken(newToken);
		sessionStorage.setItem(TOKEN_SESSION, newToken);
		setStructureData(data);
		if (refuseToBeAdmin) {
			setRole('user');
		}
	};

	const handleSessionAuth = (data: ParsedStructureData) => {
		setStructureData(data);
		setIsSessionAuth(true);
	};

	const handleCheckPolitiqueConfidentialite = (
		state: PolitiqueConfidentialiteEnum,
	) => {
		setPolitiqueConfidentialite(state);
		localStorage.setItem(POLITIQUE_CONFIDENTIALITE_SESSION, state);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de handleResetAuth
	React.useEffect(() => {
		const { origin } = window.location;

		const requestInterceptorId = request.interceptors.request.use(
			(config) => {
				// Ne jamais injecter de credentials vers un domaine tiers
				const isSameOrigin =
					!config.url?.startsWith('http') || config.url.startsWith(origin);
				if (!isSameOrigin) return config;

				const effectiveToken = token || sessionStorage.getItem(TOKEN_SESSION);
				if (effectiveToken && !config.headers.Authorization) {
					config.headers.Authorization = `Bearer ${effectiveToken}`;
				}

				const codeClient = sessionStorage.getItem(CODE_CLIENT_SESSION);
				const codePostal = sessionStorage.getItem(CODE_POSTAL_SESSION);
				const codeAcces = sessionStorage.getItem(CODE_ACCES_SESSION);

				if (codeClient && !config.headers['X-CodeClient']) {
					config.headers['X-CodeClient'] = codeClient;
				}
				if (codePostal && !config.headers.codePostal) {
					config.headers.codePostal = codePostal;
				}
				if (codeAcces && !config.headers.codeAcces) {
					config.headers.codeAcces = codeAcces;
				}

				return config;
			},
			(error) => Promise.reject(error),
		);

		const responseInterceptorId = request.interceptors.response.use(
			(response) => response,
			(error) => {
				const url = error.config?.url || '';
				const isModalAdminCall =
					url.includes('/demandeCompte/statistiques') ||
					url.includes('/demandeCompteAllClients');
				if (
					error.response &&
					error.response.status === 401 &&
					!isModalAdminCall
				) {
					handleResetAuth();
				}

				return Promise.reject(error);
			},
		);

		return () => {
			request.interceptors.request.eject(requestInterceptorId);
			request.interceptors.response.eject(responseInterceptorId);
		};
	}, [token]);

	if (politiqueConfidentialite !== PolitiqueConfidentialiteEnum.ACCEPTED) {
		return (
			<PolitiqueConfidentialite
				onAccept={handleCheckPolitiqueConfidentialite}
			/>
		);
	}

	if (!token && !isSessionAuth) {
		return (
			<Login
				onAuth={handleAuth}
				onSessionAuth={handleSessionAuth}
				userRole={role}
				setUserRole={setRole}
			/>
		);
	}

	if (!structureData) {
		return job ? (
			<TraitementLong
				title="Chargement en cours"
				identifiantJob={job.identifiant}
				onFinish={handleRefreshJobFinish}
				onError={handleRefreshJobError}
				url="/api/job360"
			/>
		) : (
			<div>Chargement...</div>
		);
	}

	return (
		<>
			<App
				name="Parametrage ixchange"
				code="HOL_PARIX"
				group="finances"
				context={Context as React.FunctionComponent}
				request={request}
				menu={false}
				shellBar={false}
			>
				<Menu.Home dataTestId="ongletAccueil">
					<Feature name="Accueil">
						<Feature.Content>
							<Sentry.ErrorBoundary fallback={errorBoundaryFallback}>
								<IxChangeContext
									userRole={role}
									backendVersion={structureData?.version ?? ''}
									initialUtilisateurs={structureData?.utilisateurs ?? []}
									initialCollectivites={structureData?.collectivites ?? []}
									onLogout={handleLogout}
									onChangeClient={
										role === 'admin' ? handleChangeClient : undefined
									}
								/>
							</Sentry.ErrorBoundary>
						</Feature.Content>
					</Feature>
				</Menu.Home>
			</App>
			{isChangingClient && (
				<ModalAdmin
					onValidate={(newCodeClient) =>
						handleChangeClientValidate(newCodeClient)
					}
					onCancel={handleCancelChangeClient}
				/>
			)}
		</>
	);
};
export default AppIxchange;
